import type { StaticPageScriptDescriptor } from "@/services/static-page-service/index"

const HTTP_PROTOCOL = "http:"
const HTTPS_PROTOCOL = "https:"
const SCRIPT_TAG_SELECTOR = "script"
const INLINE_EVENT_HANDLER_PREFIX = "on"
const URL_ATTRIBUTE_NAMES = new Set(["href", "src", "xlink:href"])
const SCRIPT_EXECUTION_REGISTRY_KEY = "__staticPageScriptExecutionRegistry__"

interface ScriptExecutionRegistry {
  external: Set<string>
  inline: Set<string>
}

const createScriptExecutionRegistry = (): ScriptExecutionRegistry => {
  return {
    external: new Set<string>(),
    inline: new Set<string>(),
  }
}

const getScriptExecutionRegistry = (
  shadowRoot: ShadowRoot
): ScriptExecutionRegistry => {
  const defaultRegistry = createScriptExecutionRegistry()
  const view = shadowRoot.ownerDocument.defaultView

  if (!view) {
    return defaultRegistry
  }

  const registryHost = view as Window & {
    [SCRIPT_EXECUTION_REGISTRY_KEY]?: ScriptExecutionRegistry
  }

  if (!registryHost[SCRIPT_EXECUTION_REGISTRY_KEY]) {
    registryHost[SCRIPT_EXECUTION_REGISTRY_KEY] = defaultRegistry
  }

  return registryHost[SCRIPT_EXECUTION_REGISTRY_KEY]
}

const isSafeHttpUrl = (value: string, baseUrl: string): boolean => {
  try {
    const url = new URL(value, baseUrl)
    return url.protocol === HTTP_PROTOCOL || url.protocol === HTTPS_PROTOCOL
  } catch {
    return false
  }
}

const sanitizeMarkupHtml = (documentNode: Document, html: string): string => {
  const template = documentNode.createElement("template")
  const baseUrl = documentNode.baseURI
  template.innerHTML = html

  for (const scriptElement of template.content.querySelectorAll(
    SCRIPT_TAG_SELECTOR
  )) {
    scriptElement.remove()
  }

  for (const element of template.content.querySelectorAll("*")) {
    const attributeNames = element.getAttributeNames()

    for (const attributeName of attributeNames) {
      const normalizedName = attributeName.toLowerCase()

      if (normalizedName.startsWith(INLINE_EVENT_HANDLER_PREFIX)) {
        element.removeAttribute(attributeName)
        continue
      }

      if (!URL_ATTRIBUTE_NAMES.has(normalizedName)) {
        continue
      }

      const attributeValue = element.getAttribute(attributeName)

      if (!attributeValue) {
        continue
      }

      if (!isSafeHttpUrl(attributeValue, baseUrl)) {
        element.removeAttribute(attributeName)
      }
    }
  }

  return template.innerHTML
}

export const clearShadowRoot = (shadowRoot: ShadowRoot): void => {
  while (shadowRoot.firstChild) {
    shadowRoot.removeChild(shadowRoot.firstChild)
  }
}

export const mountStylesheets = (
  shadowRoot: ShadowRoot,
  stylesheets: string[]
): void => {
  const baseUrl = shadowRoot.ownerDocument.baseURI

  for (const href of stylesheets) {
    if (!isSafeHttpUrl(href, baseUrl)) {
      continue
    }

    const link = shadowRoot.ownerDocument.createElement("link")
    link.rel = "stylesheet"
    link.href = href
    shadowRoot.appendChild(link)
  }
}

export const mountInlineStyles = (
  shadowRoot: ShadowRoot,
  inlineStyles: string[]
): void => {
  for (const css of inlineStyles) {
    if (!css.trim()) {
      continue
    }

    const style = shadowRoot.ownerDocument.createElement("style")
    style.textContent = css
    shadowRoot.appendChild(style)
  }
}

export const mountHtml = (shadowRoot: ShadowRoot, html: string): void => {
  const content = shadowRoot.ownerDocument.createElement("div")
  content.innerHTML = sanitizeMarkupHtml(shadowRoot.ownerDocument, html)
  shadowRoot.appendChild(content)
}

export const mountScripts = (
  shadowRoot: ShadowRoot,
  scripts: StaticPageScriptDescriptor[],
  options?: { signal?: AbortSignal }
): void => {
  const signal = options?.signal
  const baseUrl = shadowRoot.ownerDocument.baseURI
  const executionRegistry = getScriptExecutionRegistry(shadowRoot)

  const createScriptElement = (
    scriptDescriptor: StaticPageScriptDescriptor
  ): HTMLScriptElement => {
    const script = shadowRoot.ownerDocument.createElement("script")

    if (scriptDescriptor.type) {
      script.type = scriptDescriptor.type
    }

    if (scriptDescriptor.defer) {
      script.defer = true
    }

    if (scriptDescriptor.async) {
      script.async = true
    } else {
      script.async = false
    }

    if (scriptDescriptor.src) {
      script.src = scriptDescriptor.src
    } else {
      script.textContent = scriptDescriptor.inlineCode ?? ""
    }

    return script
  }

  const shouldSkipScriptDescriptor = (
    scriptDescriptor: StaticPageScriptDescriptor
  ): boolean => {
    if (!(scriptDescriptor.src || scriptDescriptor.inlineCode)) {
      return true
    }

    if (scriptDescriptor.src && !isSafeHttpUrl(scriptDescriptor.src, baseUrl)) {
      return true
    }

    return false
  }

  const isSequentialExternalScript = (
    scriptDescriptor: StaticPageScriptDescriptor
  ): boolean => {
    return Boolean(scriptDescriptor.src) && scriptDescriptor.async !== true
  }

  const getInlineScriptKey = (
    scriptDescriptor: StaticPageScriptDescriptor
  ): string => {
    return `${scriptDescriptor.type ?? "text/javascript"}:${scriptDescriptor.inlineCode ?? ""}`
  }

  const isRepeatedScript = (
    scriptDescriptor: StaticPageScriptDescriptor
  ): boolean => {
    if (scriptDescriptor.src) {
      if (executionRegistry.external.has(scriptDescriptor.src)) {
        return true
      }

      executionRegistry.external.add(scriptDescriptor.src)
      return false
    }

    const inlineScriptKey = getInlineScriptKey(scriptDescriptor)

    if (executionRegistry.inline.has(inlineScriptKey)) {
      return true
    }

    executionRegistry.inline.add(inlineScriptKey)
    return false
  }

  const mountSequentialExternalScript = async (
    script: HTMLScriptElement
  ): Promise<void> => {
    await new Promise<void>((resolve) => {
      let isResolved = false

      const cleanup = (): void => {
        script.removeEventListener("load", complete)
        script.removeEventListener("error", complete)
        signal?.removeEventListener("abort", handleAbort)
      }

      const complete = (): void => {
        if (isResolved) {
          return
        }

        isResolved = true
        cleanup()
        resolve()
      }

      const handleAbort = (): void => {
        if (script.isConnected) {
          script.remove()
        }

        complete()
      }

      if (signal?.aborted) {
        handleAbort()
        return
      }

      script.addEventListener("load", complete)
      script.addEventListener("error", complete)
      signal?.addEventListener("abort", handleAbort)

      if (signal?.aborted) {
        handleAbort()
        return
      }

      shadowRoot.appendChild(script)
    })
  }

  const runMountScripts = async (): Promise<void> => {
    for (const scriptDescriptor of scripts) {
      if (signal?.aborted) {
        return
      }

      if (shouldSkipScriptDescriptor(scriptDescriptor)) {
        continue
      }

      if (isRepeatedScript(scriptDescriptor)) {
        continue
      }

      const script = createScriptElement(scriptDescriptor)

      if (isSequentialExternalScript(scriptDescriptor)) {
        await mountSequentialExternalScript(script)
        continue
      }

      if (signal?.aborted) {
        return
      }

      shadowRoot.appendChild(script)
    }
  }

  runMountScripts().catch(() => undefined)
}
